// Library Methods
import { useTranslation } from "react-i18next";
import { useMemo } from "react";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

// Constants
import { sexTypeEnums } from "src/constants/enums";

// Types
import { PatientType } from "src/services/patients/types";

// Utils
import { calculateYearDifference } from "src/utils/date/dateHelper";
import { getDecimalNum } from "src/utils/number/numberHelper";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

type PatientInfoSectionType = {
  patient: PatientType;
};

const PatientInfoSection = ({ patient }: PatientInfoSectionType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();

  const formattedAge = useMemo(
    () => calculateYearDifference(patient?.dateOfBirth, clinicInfo?.timezone),
    [patient?.dateOfBirth, clinicInfo?.timezone]
  );

  const formattedSex = useMemo(() => {
    const key = patient?.sex?.toUpperCase() as keyof typeof sexTypeEnums;
    return t(sexTypeEnums[key]);
  }, [patient, t]);

  const formattedBirthdate = useMemo(
    () => patient?.dateOfBirth?.slice(0, 10) ?? "N/A",
    [patient?.dateOfBirth]
  );

  const uniqueId = useMemo(
    () =>
      typeof patient?.healthCareId === "string" &&
      patient?.healthCareId?.length > 0
        ? patient?.healthCareId
        : t("Blank"),
    [patient?.healthCareId, t]
  );

  const formattedCaptions = useMemo(
    () => (patient?.requiresCaptions ? t("Yes") : t("No")),
    [patient?.requiresCaptions, t]
  );

  const emptyLeftPrescription = useMemo(
    () =>
      patient?.leftSphere === null &&
      patient?.leftCylinder === null &&
      patient?.leftAxis === null,
    [patient?.leftSphere, patient?.leftCylinder, patient?.leftAxis]
  );

  const emptyRightPrescription = useMemo(
    () =>
      patient?.rightSphere === null &&
      patient?.rightCylinder === null &&
      patient?.rightAxis === null,
    [patient?.rightSphere, patient?.rightCylinder, patient?.rightAxis]
  );

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        columnGap: "16px",
        rowGap: "0px",
      }}
    >
      {/* Column 1: non-prescription fields */}
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: "8px" }}>
          <Typography fontSize="13px">
            <span style={{ fontWeight: "bold" }}>{t("Age")}: </span>
            <span>{formattedAge}</span>
          </Typography>
          <Typography fontSize="13px">
            <span style={{ fontWeight: "bold" }}>{t("Sex")}: </span>
            <span>{formattedSex}</span>
          </Typography>
          <Typography fontSize="13px">
            <span style={{ fontWeight: "bold" }}>{t("Birthdate")}: </span>
            <span>{formattedBirthdate}</span>
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: "8px" }}>
          <Typography fontSize="13px">
            <span style={{ fontWeight: "bold" }}>{t("Healthcare Id")}: </span>
            <span>{uniqueId}</span>
          </Typography>
          <Typography fontSize="13px">
            <span style={{ fontWeight: "bold" }}>
              {t("Requires Captions")}:{" "}
            </span>
            <span>{formattedCaptions}</span>
          </Typography>
        </Box>
      </Box>

      {/* Column 2: prescription fields */}
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Typography fontSize="13px">
          <span style={{ fontWeight: "bold" }}>{t("OD Rx")}: </span>
          {emptyRightPrescription ? (
            <span>{t("No Prescription Entered")}</span>
          ) : (
            <span>
              {patient?.rightSphere !== null
                ? getDecimalNum(patient?.rightSphere, 2)
                : "N/A"}
              {" sph | "}
              {patient?.rightCylinder !== null
                ? getDecimalNum(patient?.rightCylinder, 2)
                : "N/A"}
              {" cyl | "}
              {patient?.rightAxis !== null
                ? `${getDecimalNum(patient?.rightAxis, 0)}°`
                : "N/A"}
              {` ${t("axis")}`}
            </span>
          )}
        </Typography>

        <Typography fontSize="13px">
          <span style={{ fontWeight: "bold" }}>{t("OS Rx")}: </span>
          {emptyLeftPrescription ? (
            <span>{t("No Prescription Entered")}</span>
          ) : (
            <span>
              {patient?.leftSphere !== null
                ? getDecimalNum(patient?.leftSphere, 2)
                : "N/A"}
              {" sph | "}
              {patient?.leftCylinder !== null
                ? getDecimalNum(patient?.leftCylinder, 2)
                : "N/A"}
              {" cyl | "}
              {patient?.leftAxis !== null
                ? `${getDecimalNum(patient?.leftAxis, 0)}°`
                : "N/A"}
              {` ${t("axis")}`}
            </span>
          )}
        </Typography>
      </Box>
    </Box>
  );
};

export default PatientInfoSection;
